import { useState, useCallback, useEffect, useRef } from 'react';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Send, MessageSquareOff } from 'lucide-react';
import type { NostrEvent } from '@nostrify/nostrify';

interface Conversation {
  peerPubkey: string;
  lastMessage: string;
  lastTime: number;
  unread: number;
}

function ConversationList({
  conversations,
  selectedPeer,
  onSelect,
}: {
  conversations: Conversation[];
  selectedPeer: string | null;
  onSelect: (peer: string) => void;
}) {
  return (
    <div className="w-44 border-r border-border/50 overflow-y-auto">
      {conversations.map((conv) => {
        return (
          <ConversationItem
            key={conv.peerPubkey}
            peerPubkey={conv.peerPubkey}
            lastMessage={conv.lastMessage}
            lastTime={conv.lastTime}
            isSelected={selectedPeer === conv.peerPubkey}
            onClick={() => onSelect(conv.peerPubkey)}
          />
        );
      })}
    </div>
  );
}

function ConversationItem({
  peerPubkey,
  lastMessage,
  isSelected,
  onClick,
}: {
  peerPubkey: string;
  lastMessage: string;
  lastTime: number;
  isSelected: boolean;
  onClick: () => void;
}) {
  const author = useAuthor(peerPubkey);
  const metadata = author.data?.metadata;
  const displayName = metadata?.name ?? metadata?.display_name ?? genUserName(peerPubkey);

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-2.5 text-left transition-colors hover:bg-muted/50 ${
        isSelected ? 'bg-muted' : ''
      }`}
    >
      <Avatar className="w-8 h-8 shrink-0">
        <AvatarImage src={metadata?.picture} alt={displayName} />
        <AvatarFallback className="text-xs">{displayName.slice(0, 2).toUpperCase()}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium truncate">{displayName}</div>
        <div className="text-[10px] text-muted-foreground truncate">{lastMessage.slice(0, 30)}</div>
      </div>
    </button>
  );
}

function ChatView({ peerPubkey }: { peerPubkey: string }) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const [messageText, setMessageText] = useState('');
  const [messages, setMessages] = useState<NostrEvent[]>([]);
  const [isSending, setIsSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const author = useAuthor(peerPubkey);
  const metadata = author.data?.metadata;
  const displayName = metadata?.name ?? metadata?.display_name ?? genUserName(peerPubkey);

  const { data: fetchedMessages } = useQuery({
    queryKey: ['dms', peerPubkey, user?.pubkey],
    queryFn: async ({ signal }) => {
      if (!user) return [];
      const events = await nostr.query(
        [
          { kinds: [4], authors: [user.pubkey], '#p': [peerPubkey], limit: 100 },
          { kinds: [4], authors: [peerPubkey], '#p': [user.pubkey], limit: 100 },
        ],
        { signal }
      );
      return events.sort((a, b) => a.created_at - b.created_at);
    },
    enabled: !!user && !!peerPubkey,
    refetchInterval: 5000,
  });

  useEffect(() => {
    if (fetchedMessages) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMessages(fetchedMessages);
    }
  }, [fetchedMessages]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = useCallback(async () => {
    if (!user || !messageText.trim() || !user.signer.nip04) return;
    
    setIsSending(true);
    try {
      const ciphertext = await user.signer.nip04.encrypt(peerPubkey, messageText.trim());
      const event = await user.signer.signEvent({
        kind: 4,
        content: ciphertext,
        tags: [['p', peerPubkey]],
        created_at: Math.floor(Date.now() / 1000),
      });
      await nostr.event(event);
      setMessages((prev) => [...prev, event]);
      setMessageText('');
    } catch (e) {
      console.error('Failed to send DM:', e);
    } finally {
      setIsSending(false);
    }
  }, [user, peerPubkey, messageText, nostr]);

  const decryptMessage = useCallback(async (event: NostrEvent) => {
    if (!user?.signer.nip04) return '[Encrypted]';
    try {
      const plaintext = await user.signer.nip04.decrypt(
        event.pubkey === user.pubkey ? peerPubkey : event.pubkey,
        event.content
      );
      return plaintext;
    } catch {
      return '[Encrypted]';
    }
  }, [user, peerPubkey]);

  const [decryptedTexts, setDecryptedTexts] = useState<Record<string, string>>({});

  // Decrypt in one batch — was one setState (and re-render) per message.
  useEffect(() => {
    const missing = messages.filter((m) => decryptedTexts[m.id] === undefined);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        missing.map(async (m) => [m.id, await decryptMessage(m)] as const),
      );
      if (cancelled) return;
      setDecryptedTexts((prev) => {
        const next = { ...prev };
        for (const [id, text] of entries) next[id] = text;
        return next;
      });
    })();
    return () => { cancelled = true; };
  }, [messages, decryptMessage, decryptedTexts]);

  if (!user) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center text-muted-foreground text-sm">
          <MessageSquareOff className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>Login to send messages</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-border/50 bg-muted/30">
        <Avatar className="w-7 h-7">
          <AvatarImage src={metadata?.picture} alt={displayName} />
          <AvatarFallback className="text-xs">{displayName.slice(0, 2).toUpperCase()}</AvatarFallback>
        </Avatar>
        <span className="text-sm font-medium">{displayName}</span>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-auto px-4 py-2 space-y-2">
        {messages.map((msg) => {
          const isMe = msg.pubkey === user.pubkey;
          return (
            <div
              key={msg.id}
              className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] px-3 py-2 rounded-xl text-sm ${
                  isMe
                    ? 'bg-primary text-primary-foreground rounded-br-sm'
                    : 'bg-muted rounded-bl-sm'
                }`}
              >
                {decryptedTexts[msg.id] ?? (
                  <Skeleton className="h-4 w-20" />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Input */}
      <div className="flex items-center gap-2 px-3 py-2 border-t border-border/50">
        <Input
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
          placeholder="Type a message..."
          className="flex-1 h-9 text-sm"
        />
        <Button
          size="icon"
          className="h-9 w-9"
          onClick={sendMessage}
          disabled={!messageText.trim() || isSending}
        >
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export function DMsApp() {
  const { user } = useCurrentUser();
  const { nostr } = useNostr();
  const [selectedPeer, setSelectedPeer] = useState<string | null>(null);

  const { data: dmEvents } = useQuery({
    queryKey: ['dms', 'all'],
    queryFn: async ({ signal }) => {
      if (!user) return [];
      return nostr.query(
        [
          { kinds: [4], authors: [user.pubkey], limit: 200 },
          { kinds: [4], '#p': [user.pubkey], limit: 200 },
        ],
        { signal }
      );
    },
    enabled: !!user,
  });

  const conversations: Conversation[] = (() => {
    if (!dmEvents || !user) return [];
    const convMap = new Map<string, Conversation>();

    dmEvents.forEach((event) => {
      const isMe = event.pubkey === user.pubkey;
      const peerPubkey = isMe
        ? event.tags.find(([n]) => n === 'p')?.[1]
        : event.pubkey;

      if (!peerPubkey) return;

      const existing = convMap.get(peerPubkey);
      if (!existing || event.created_at > existing.lastTime) {
        convMap.set(peerPubkey, {
          peerPubkey,
          lastMessage: '[Encrypted]',
          lastTime: event.created_at,
          unread: 0,
        });
      }
    });

    return Array.from(convMap.values()).sort((a, b) => b.lastTime - a.lastTime);
  })();

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <MessageSquareOff className="w-12 h-12 mb-3 opacity-40" />
        <p className="text-sm">Login to view direct messages</p>
      </div>
    );
  }

  return (
    <div className="flex h-full">
      <ConversationList
        conversations={conversations}
        selectedPeer={selectedPeer}
        onSelect={setSelectedPeer}
      />
      {selectedPeer ? (
        <ChatView peerPubkey={selectedPeer} />
      ) : (
        <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
          Select a conversation to start chatting
        </div>
      )}
    </div>
  );
}
